import React, {useCallback, useState} from 'react';
import {useParams} from 'react-router-dom';

import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useSystemAccounts} from 'src/hooks/account/system-accounts';
import {CREATE_USER_STEPS} from 'src/modules/profile/account/create-user/constants';
import {getAccountActiveUser} from 'src/openapi/admin/apis/GetAccountActiveUserApi';
import {useAppDispatch} from 'src/store/hooks';

import {CreateUserDetailsStep} from './steps/user-details';
import {CreateUserLimitsStep} from './steps/user-limits';
import {CreateUserRoleSettingsStep} from './steps/user-role-settings/user-role-settings.step';
import {CreateUserSettingsStep} from './steps/users-settings';

export const CreateUserPage = () => {
    const apiDispatch = useAppDispatch();
    const {hasAccess} = useAdminAccessRight();
    const {isAdminAccount} = useSystemAccounts();
    const queryParams = useParams<{accountId: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;
    const [selectedStep, setSelectedStep] = useState(CREATE_USER_STEPS.USER_DETAILS);
    const [createdUserId, setCreatedUserId] = useState<number | undefined>(undefined);

    const canSetupAdminRole = isAdminAccount(accountId) && hasAccess(ACCESS_RIGHTS.MANAGE_PERMISSIONS_FOR_A_PROFILE);
    const canViewLimits = hasAccess(ACCESS_RIGHTS.USER_LIMITS_VIEW);

    const onUserCreate = useCallback((userId: number) => {
        setCreatedUserId(userId);
        apiDispatch(getAccountActiveUser({accountId}));
    }, [setCreatedUserId, apiDispatch, accountId]);

    if (selectedStep === CREATE_USER_STEPS.USER_DETAILS) {
        return (
            <CreateUserDetailsStep
                userId={createdUserId}
                canSetupAdminRole={canSetupAdminRole}
                canViewLimits={canViewLimits}
                onUserCreate={onUserCreate}
                onUserDetailsSet={() => setSelectedStep(CREATE_USER_STEPS.SETTINGS)}
            />
        );
    }

    if (selectedStep === CREATE_USER_STEPS.SETTINGS && createdUserId) {
        let onNext;

        if (canViewLimits && canSetupAdminRole) {
            onNext = () => setSelectedStep(CREATE_USER_STEPS.LIMITS);
        } else if (canSetupAdminRole) {
            onNext = () => setSelectedStep(CREATE_USER_STEPS.ROLE_SETTINGS);
        } else if (canViewLimits) {
            onNext = () => setSelectedStep(CREATE_USER_STEPS.LIMITS);
        }

        return (
            <CreateUserSettingsStep
                userId={createdUserId}
                canViewLimits={canViewLimits}
                canSetupAdminRole={canSetupAdminRole}
                onNext={onNext}
                onBack={() => setSelectedStep(CREATE_USER_STEPS.USER_DETAILS)}
                onRedirectToStep={setSelectedStep}
            />
        );
    }

    if (selectedStep === CREATE_USER_STEPS.LIMITS && createdUserId) {
        let onNext;

        if (canSetupAdminRole) {
            onNext = () => setSelectedStep(CREATE_USER_STEPS.ROLE_SETTINGS);
        }

        return (
            <CreateUserLimitsStep
                userId={createdUserId}
                canSetupAdminRole={canSetupAdminRole}
                canViewLimits={canViewLimits}
                onNext={onNext}
                onBack={() => setSelectedStep(CREATE_USER_STEPS.SETTINGS)}
                onRedirectToStep={setSelectedStep}
            />
        );
    }

    if (selectedStep === CREATE_USER_STEPS.ROLE_SETTINGS && createdUserId) {
        let onBack = () => setSelectedStep(CREATE_USER_STEPS.SETTINGS);
        if (canViewLimits) {
            onBack = () => setSelectedStep(CREATE_USER_STEPS.LIMITS);
        }

        return (
            <CreateUserRoleSettingsStep
                userId={createdUserId}
                canViewLimits={canViewLimits}
                canSetupAdminRole={canSetupAdminRole}
                onBack={onBack}
                onRedirectToStep={setSelectedStep}
            />
        );
    }

    return null;
};
